import { useRef, type RefObject } from "react"
import { IconLoader2 } from "@tabler/icons-react"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { useBenefitDraftState, useBenefitReviewActive, useBenefitSaving } from "@/features/benefit-workspace/benefitEditorContext"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import { api } from "@/lib/api"
import { saveBenefitChange } from "@/lib/benefit-save"
import type { BenefitDetailDto } from "@/lib/benefit-types"
import { captureMemberRemoval, memberRemovalCurrent, memberRemovalPath } from "@/lib/member-removal"
import { formatPersonName } from "@/lib/person-identity"
import { useHasOpenBenefitEditors } from "./benefit-edit-scope"

/** One confirmation outside the roster: a removed or ambiguous row cannot unhost it. */
export function useMemberRemoval({ benefit, canEdit, onUpdated, section }: {
  benefit: BenefitDetailDto; canEdit: boolean; onUpdated(updated: BenefitDetailDto): void; section: RefObject<HTMLElement | null>
}) {
  const [editing, setEditing, token] = useBenefitEdit(benefit.concurrencyToken, "remove-member")
  const [confirmation, setConfirmation] = useBenefitDraftState("remove-member", "confirmation", null)
  const [saving, setSaving] = useBenefitSaving()
  const reviewing = useBenefitReviewActive()
  const reviewingRef = useRef(reviewing); reviewingRef.current = reviewing
  const hasEditor = useHasOpenBenefitEditors()
  const origin = useRef<HTMLElement | null>(null)
  const targetId = useRef<string | null>(null)
  const completed = useRef(false)
  if (editing && confirmation) targetId.current = confirmation.member.personId
  const restoreFocus = () => {
    if (!section.current?.isConnected || reviewingRef.current) return
    const usable = (element: HTMLElement | null | undefined) => element?.isConnected && element.getClientRects().length && !element.matches(":disabled")
    const panel = [...document.querySelectorAll<HTMLElement>("[data-member-panel]")].find(element => element.dataset.memberPanel === targetId.current)
    const row = [...section.current.querySelectorAll<HTMLElement>("[data-member-row]")].find(element => element.dataset.memberRow === targetId.current)
    const panelAction = panel?.querySelector<HTMLElement>("[data-member-remove]")
    const rowAction = row?.querySelector<HTMLElement>("[data-destructive]") ?? row?.querySelector<HTMLElement>("button")
    const target = !completed.current && usable(origin.current) ? origin.current
      : !completed.current && usable(panelAction) ? panelAction
      : !completed.current && usable(rowAction) ? rowAction : section.current.querySelector<HTMLElement>("[data-member-heading]")
    target?.focus()
  }
  const open = (personId: string, opener: HTMLElement) => {
    if (!canEdit || saving || reviewing || hasEditor) return
    const captured = captureMemberRemoval(benefit, personId)
    if (!captured) return
    origin.current = opener; completed.current = false
    setConfirmation(captured)
    setEditing(true)
  }
  const close = () => { if (!saving && !reviewing && setEditing(false)) setConfirmation(null) }
  const current = editing && canEdit && !reviewing && !!confirmation && token === confirmation.concurrencyToken && memberRemovalCurrent(confirmation, benefit)
  const remove = async () => {
    if (saving || !current || !confirmation) return
    setSaving(true)
    const { saved, reloaded } = await saveBenefitChange(benefit.id, onUpdated,
      () => api.del<BenefitDetailDto>(memberRemovalPath(confirmation)), "Household member removed.")
    setSaving(false)
    // Workspace conflicts/unknown outcomes stay with the command owner. Standalone
    // callers preserve their existing successful-409-reload/discard behavior.
    if (saved || reloaded) { completed.current = true; setEditing(false); restoreFocus() }
  }
  return { open, editing, dialog: <AlertDialog open={editing && !!confirmation && !reviewing} onOpenChange={value => { if (!value) close() }}>
    <AlertDialogContent onCloseAutoFocus={event => { event.preventDefault(); restoreFocus() }}>
      <AlertDialogHeader>
        <AlertDialogTitle>Remove {confirmation ? formatPersonName(confirmation.member) : "household member"}?</AlertDialogTitle>
        <AlertDialogDescription>They stop being part of this application's household and their income on this application is removed with them. The person record itself is kept.</AlertDialogDescription>
      </AlertDialogHeader>
      {!current && <p role="status" className="text-sm">This removal is not currently available. The original member, income and token are retained; cancel or refresh the saved data.</p>}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={saving} className="min-h-11 sm:min-h-9">Cancel</AlertDialogCancel>
        <AlertDialogAction disabled={saving || !current} variant="destructive" className="min-h-11 sm:min-h-9"
          onClick={event => { event.preventDefault(); void remove() }}>
          {saving && <IconLoader2 className="mr-1.5 size-4 animate-spin" />}Remove
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog> }
}
